import React, { useEffect, useRef } from 'react';
import ColorPicker from '../LoaderGenerator/ColorPicker';
import {
  ALERT_TYPES,
  ANIMATION_TYPES,
  BUTTON_STYLES,
  getAlertTypeConfig,
  getEffectiveColor,
} from './alertConfig';

const AlertControls = ({ config, onChange }) => {
  const previousTypeRef = useRef(config.type || 'success');

  const handleChange = (key, value) => {
    onChange({ [key]: value });
  };

  // Reactive type switching - auto-update defaults when type changes
  useEffect(() => {
    const currentType = config.type || 'success';
    const previousType = previousTypeRef.current;

    if (currentType !== previousType) {
      const previousTypeConfig = getAlertTypeConfig(previousType);
      const currentTypeConfig = getAlertTypeConfig(currentType);
      const updates = {};

      // Only update if current values match previous type defaults (preserve user customizations)
      if (
        !config.title ||
        config.title === previousTypeConfig.defaultTitle ||
        config.title === ''
      ) {
        updates.title = currentTypeConfig.defaultTitle;
      }
      if (
        !config.message ||
        config.message === previousTypeConfig.defaultMessage ||
        config.message === ''
      ) {
        updates.message = currentTypeConfig.defaultMessage;
      }
      if (
        !config.primaryButtonText ||
        config.primaryButtonText === previousTypeConfig.defaultPrimaryButton ||
        config.primaryButtonText === ''
      ) {
        updates.primaryButtonText = currentTypeConfig.defaultPrimaryButton;
      }

      if (Object.keys(updates).length > 0) {
        onChange(updates);
      }

      previousTypeRef.current = currentType;
    }
  }, [config.type, config.title, config.message, config.primaryButtonText, onChange]);

  const alertTypesArray = Object.entries(ALERT_TYPES).map(([value, data]) => ({
    value,
    ...data,
  }));

  return (
    <div className="space-y-6">
      {/* Alert Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Alert Type
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {alertTypesArray.map((type) => (
            <button
              key={type.value}
              onClick={() => {
                previousTypeRef.current = config.type || 'success';
                handleChange('type', type.value);
              }}
              className={`
                px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap
                ${
                  (config.type || 'success') === type.value
                    ? 'bg-gradient-primary text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }
              `}
            >
              {type.label}
            </button>
          ))}
        </div>
        {/* Custom Color Picker for Custom Type */}
        {config.type === 'custom' && (
          <div className="mt-3">
            <ColorPicker
              color={config.customTypeColor || ALERT_TYPES.custom.defaultColor}
              onChange={(color) => handleChange('customTypeColor', color)}
              label="Custom Type Color"
            />
          </div>
        )}
      </div>

      {/* Icon Settings */}
      <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Icon Settings
        </h3>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.showIcon !== false}
              onChange={(e) => handleChange('showIcon', e.target.checked)}
              className="rounded"
            />
            Show Icon
          </label>
          {config.showIcon !== false && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom Icon (leave empty for default)
              </label>
              <input
                type="text"
                value={config.customIcon || ''}
                onChange={(e) => handleChange('customIcon', e.target.value || null)}
                placeholder={getAlertTypeConfig(config.type || 'success').defaultIcon}
                className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Content
        </h3>
        <div className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Title
            </label>
            <input
              type="text"
              value={config.title || ''}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Message
            </label>
            <textarea
              value={config.message || ''}
              onChange={(e) => handleChange('message', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm resize-none"
            />
          </div>
        </div>
      </div>

      {/* Button Configuration */}
      <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg mb-6">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
          Buttons
        </h3>
        <div className="space-y-4">
          {/* Button Count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Number of Buttons: {config.buttonCount || 1}
            </label>
            <input
              type="range"
              min="1"
              max="3"
              value={config.buttonCount || 1}
              onChange={(e) => handleChange('buttonCount', parseInt(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Primary Button */}
          {config.buttonCount >= 1 && (
            <div className="space-y-4 p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 pb-2">
                Primary Button
              </h4>
              <div className="space-y-4">
                {/* Button Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={config.primaryButtonText || ''}
                    onChange={(e) => handleChange('primaryButtonText', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
                  />
                </div>

                {/* Style and Tint Color */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Style
                    </label>
                    <select
                      value={config.primaryButtonStyle || 'borderedProminent'}
                      onChange={(e) => handleChange('primaryButtonStyle', e.target.value)}
                      className="w-full px-2 py-1.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-xs"
                    >
                      {BUTTON_STYLES.map((style) => (
                        <option key={style.value} value={style.value}>
                          {style.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <ColorPicker
                      color={config.primaryButtonColor || getEffectiveColor(config)}
                      onChange={(color) => handleChange('primaryButtonColor', color)}
                      label="Tint Color"
                    />
                  </div>
                </div>

                {/* Background Color */}
                <div>
                  <ColorPicker
                    color={config.primaryButtonBackgroundColor || '#ffffff'}
                    onChange={(color) => handleChange('primaryButtonBackgroundColor', color)}
                    label="Background Color"
                  />
                </div>

                {/* Text Color */}
                <div>
                  <ColorPicker
                    color={config.primaryButtonTextColor || '#000000'}
                    onChange={(color) => handleChange('primaryButtonTextColor', color)}
                    label="Text Color"
                  />
                </div>

                {/* Padding */}
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Padding: {config.primaryButtonPadding !== null && config.primaryButtonPadding !== undefined ? config.primaryButtonPadding : 8}px
                  </label>
                  <input
                    type="range"
                    min="4"
                    max="24"
                    value={config.primaryButtonPadding !== null && config.primaryButtonPadding !== undefined ? config.primaryButtonPadding : 8}
                    onChange={(e) => handleChange('primaryButtonPadding', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                {/* Corner Radius */}
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Corner Radius: {config.primaryButtonCornerRadius !== null && config.primaryButtonCornerRadius !== undefined ? config.primaryButtonCornerRadius : 8}px
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="20"
                    value={config.primaryButtonCornerRadius !== null && config.primaryButtonCornerRadius !== undefined ? config.primaryButtonCornerRadius : 8}
                    onChange={(e) => handleChange('primaryButtonCornerRadius', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                {/* Border */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      checked={config.primaryButtonBorderWidth !== null && config.primaryButtonBorderWidth !== undefined && config.primaryButtonBorderWidth > 0}
                      onChange={(e) => handleChange('primaryButtonBorderWidth', e.target.checked ? 1 : null)}
                      className="rounded"
                    />
                    Enable Border
                  </label>
                  {config.primaryButtonBorderWidth !== null && config.primaryButtonBorderWidth !== undefined && config.primaryButtonBorderWidth > 0 && (
                    <div className="pl-4 space-y-2">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-500 mb-1">
                          Border Width: {config.primaryButtonBorderWidth}px
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="5"
                          value={config.primaryButtonBorderWidth}
                          onChange={(e) => handleChange('primaryButtonBorderWidth', parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>
                      <ColorPicker
                        color={config.primaryButtonBorderColor || '#cccccc'}
                        onChange={(color) => handleChange('primaryButtonBorderColor', color)}
                        label="Border Color"
                      />
                    </div>
                  )}
                </div>

                {/* Shadow */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      checked={config.primaryButtonShadowEnabled !== null ? config.primaryButtonShadowEnabled : false}
                      onChange={(e) => handleChange('primaryButtonShadowEnabled', e.target.checked)}
                      className="rounded"
                    />
                    Enable Shadow
                  </label>
                  {config.primaryButtonShadowEnabled && (
                    <div className="pl-4 space-y-2">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-500 mb-1">
                          Shadow Radius: {config.primaryButtonShadowRadius || 4}px
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="20"
                          value={config.primaryButtonShadowRadius || 4}
                          onChange={(e) => handleChange('primaryButtonShadowRadius', parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-500 mb-1">
                          Shadow Opacity: {((config.primaryButtonShadowOpacity ?? 0.2) * 100).toFixed(0)}%
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="1"
                          value={(config.primaryButtonShadowOpacity ?? 0.2) * 100}
                          onChange={(e) => handleChange('primaryButtonShadowOpacity', parseFloat(e.target.value) / 100)}
                          className="w-full"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Secondary Button */}
          {config.buttonCount >= 2 && (
            <div className="space-y-4 p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 pb-2">
                Secondary Button
              </h4>
              <div className="space-y-4">
                {/* Button Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={config.secondaryButtonText || 'Cancel'}
                    onChange={(e) => handleChange('secondaryButtonText', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
                  />
                </div>

                {/* Style */}
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Style
                  </label>
                  <select
                    value={config.secondaryButtonStyle || 'bordered'}
                    onChange={(e) => handleChange('secondaryButtonStyle', e.target.value)}
                    className="w-full px-2 py-1.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-xs"
                  >
                    {BUTTON_STYLES.map((style) => (
                      <option key={style.value} value={style.value}>
                        {style.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Background Color */}
                <div>
                  <ColorPicker
                    color={config.secondaryButtonBackgroundColor || '#ffffff'}
                    onChange={(color) => handleChange('secondaryButtonBackgroundColor', color)}
                    label="Background Color"
                  />
                </div>

                {/* Text Color */}
                <div>
                  <ColorPicker
                    color={config.secondaryButtonTextColor || '#000000'}
                    onChange={(color) => handleChange('secondaryButtonTextColor', color)}
                    label="Text Color"
                  />
                </div>

                {/* Padding */}
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Padding: {config.secondaryButtonPadding !== null && config.secondaryButtonPadding !== undefined ? config.secondaryButtonPadding : 8}px
                  </label>
                  <input
                    type="range"
                    min="4"
                    max="24"
                    value={config.secondaryButtonPadding !== null && config.secondaryButtonPadding !== undefined ? config.secondaryButtonPadding : 8}
                    onChange={(e) => handleChange('secondaryButtonPadding', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                {/* Corner Radius */}
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Corner Radius: {config.secondaryButtonCornerRadius !== null && config.secondaryButtonCornerRadius !== undefined ? config.secondaryButtonCornerRadius : 8}px
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="20"
                    value={config.secondaryButtonCornerRadius !== null && config.secondaryButtonCornerRadius !== undefined ? config.secondaryButtonCornerRadius : 8}
                    onChange={(e) => handleChange('secondaryButtonCornerRadius', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                {/* Border */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      checked={config.secondaryButtonBorderWidth !== null && config.secondaryButtonBorderWidth !== undefined && config.secondaryButtonBorderWidth > 0}
                      onChange={(e) => handleChange('secondaryButtonBorderWidth', e.target.checked ? 1 : null)}
                      className="rounded"
                    />
                    Enable Border
                  </label>
                  {config.secondaryButtonBorderWidth !== null && config.secondaryButtonBorderWidth !== undefined && config.secondaryButtonBorderWidth > 0 && (
                    <div className="pl-4 space-y-2">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-500 mb-1">
                          Border Width: {config.secondaryButtonBorderWidth}px
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="5"
                          value={config.secondaryButtonBorderWidth}
                          onChange={(e) => handleChange('secondaryButtonBorderWidth', parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>
                      <ColorPicker
                        color={config.secondaryButtonBorderColor || '#cccccc'}
                        onChange={(color) => handleChange('secondaryButtonBorderColor', color)}
                        label="Border Color"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tertiary Button */}
          {config.buttonCount >= 3 && (
            <div className="space-y-4 p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 pb-2">
                Tertiary Button
              </h4>
              <div className="space-y-4">
                {/* Button Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={config.tertiaryButtonText || 'Delete'}
                    onChange={(e) => handleChange('tertiaryButtonText', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
                  />
                </div>

                {/* Style */}
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Style
                  </label>
                  <select
                    value={config.tertiaryButtonStyle || 'bordered'}
                    onChange={(e) => handleChange('tertiaryButtonStyle', e.target.value)}
                    className="w-full px-2 py-1.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-xs"
                  >
                    {BUTTON_STYLES.map((style) => (
                      <option key={style.value} value={style.value}>
                        {style.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Background Color */}
                <div>
                  <ColorPicker
                    color={config.tertiaryButtonBackgroundColor || '#ffffff'}
                    onChange={(color) => handleChange('tertiaryButtonBackgroundColor', color)}
                    label="Background Color"
                  />
                </div>

                {/* Text Color */}
                <div>
                  <ColorPicker
                    color={config.tertiaryButtonTextColor || '#000000'}
                    onChange={(color) => handleChange('tertiaryButtonTextColor', color)}
                    label="Text Color"
                  />
                </div>

                {/* Padding */}
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Padding: {config.tertiaryButtonPadding !== null && config.tertiaryButtonPadding !== undefined ? config.tertiaryButtonPadding : 8}px
                  </label>
                  <input
                    type="range"
                    min="4"
                    max="24"
                    value={config.tertiaryButtonPadding !== null && config.tertiaryButtonPadding !== undefined ? config.tertiaryButtonPadding : 8}
                    onChange={(e) => handleChange('tertiaryButtonPadding', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                {/* Corner Radius */}
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Corner Radius: {config.tertiaryButtonCornerRadius !== null && config.tertiaryButtonCornerRadius !== undefined ? config.tertiaryButtonCornerRadius : 8}px
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="20"
                    value={config.tertiaryButtonCornerRadius !== null && config.tertiaryButtonCornerRadius !== undefined ? config.tertiaryButtonCornerRadius : 8}
                    onChange={(e) => handleChange('tertiaryButtonCornerRadius', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                {/* Border */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      checked={config.tertiaryButtonBorderWidth !== null && config.tertiaryButtonBorderWidth !== undefined && config.tertiaryButtonBorderWidth > 0}
                      onChange={(e) => handleChange('tertiaryButtonBorderWidth', e.target.checked ? 1 : null)}
                      className="rounded"
                    />
                    Enable Border
                  </label>
                  {config.tertiaryButtonBorderWidth !== null && config.tertiaryButtonBorderWidth !== undefined && config.tertiaryButtonBorderWidth > 0 && (
                    <div className="pl-4 space-y-2">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-500 mb-1">
                          Border Width: {config.tertiaryButtonBorderWidth}px
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="5"
                          value={config.tertiaryButtonBorderWidth}
                          onChange={(e) => handleChange('tertiaryButtonBorderWidth', parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>
                      <ColorPicker
                        color={config.tertiaryButtonBorderColor || '#cccccc'}
                        onChange={(color) => handleChange('tertiaryButtonBorderColor', color)}
                        label="Border Color"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Appearance */}
      <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Appearance
        </h3>
        <div className="space-y-4">
          {/* Corner Radius */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Corner Radius: {config.cornerRadius || 12}px
            </label>
            <input
              type="range"
              min="0"
              max="30"
              value={config.cornerRadius || 12}
              onChange={(e) => handleChange('cornerRadius', parseInt(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Padding */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Padding: {config.padding || 24}px
            </label>
            <input
              type="range"
              min="8"
              max="48"
              value={config.padding || 24}
              onChange={(e) => handleChange('padding', parseInt(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Shadow */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <input
                type="checkbox"
                checked={config.shadowEnabled !== false}
                onChange={(e) => handleChange('shadowEnabled', e.target.checked)}
                className="rounded"
              />
              Enable Shadow
            </label>
            {config.shadowEnabled !== false && (
              <div className="mt-2 space-y-2">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Shadow Radius: {config.shadowRadius || 20}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    value={config.shadowRadius || 20}
                    onChange={(e) => handleChange('shadowRadius', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Shadow Opacity: {((config.shadowOpacity ?? 0.1) * 100).toFixed(0)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={(config.shadowOpacity ?? 0.1) * 100}
                    onChange={(e) => handleChange('shadowOpacity', parseFloat(e.target.value) / 100)}
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Colors */}
          <ColorPicker
            color={config.backgroundColor || '#ffffff'}
            onChange={(color) => handleChange('backgroundColor', color)}
            label="Background Color"
          />

          <ColorPicker
            color={config.titleColor || '#000000'}
            onChange={(color) => handleChange('titleColor', color)}
            label="Title Color"
          />

          <ColorPicker
            color={config.messageColor || '#666666'}
            onChange={(color) => handleChange('messageColor', color)}
            label="Message Color"
          />
        </div>
      </div>

      {/* Background Blur */}
      <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Background Blur
        </h3>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.blurBackground !== false}
              onChange={(e) => handleChange('blurBackground', e.target.checked)}
              className="rounded"
            />
            Blur Background
          </label>
          {config.blurBackground !== false && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Blur Intensity: {config.blurIntensity || 10}
              </label>
              <input
                type="range"
                min="0"
                max="20"
                value={config.blurIntensity || 10}
                onChange={(e) => handleChange('blurIntensity', parseInt(e.target.value))}
                className="w-full"
              />
            </div>
          )}
        </div>
      </div>

      {/* Behavior */}
      <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Behavior
        </h3>
        <div className="space-y-4">
          {/* Dismissible */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={config.dismissible !== false}
                onChange={(e) => handleChange('dismissible', e.target.checked)}
                className="rounded"
              />
              Dismissible (tap outside to dismiss)
            </label>
          </div>

          {/* Auto Dismiss */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={config.autoDismiss === true}
                onChange={(e) => handleChange('autoDismiss', e.target.checked)}
                className="rounded"
              />
              Auto Dismiss
            </label>
            {config.autoDismiss && (
              <div className="mt-2">
                <label className="block text-xs text-gray-400 mb-1">
                  Delay: {config.autoDismissDelay || 3} seconds
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={config.autoDismissDelay || 3}
                  onChange={(e) => handleChange('autoDismissDelay', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Animation */}
      <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
          Animation
        </h3>
        <div className="space-y-5">
          {/* Animation Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Animation Type
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ANIMATION_TYPES.map((anim) => (
                <button
                  key={anim.value}
                  onClick={() => {
                    handleChange('animationType', anim.value);
                    if (anim.defaultDuration !== undefined) {
                      handleChange('animationDuration', anim.defaultDuration);
                    }
                  }}
                  className={`
                    px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap
                    ${
                      (config.animationType || 'fade') === anim.value
                        ? 'bg-gradient-primary text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }
                  `}
                >
                  {anim.label}
                </button>
              ))}
            </div>
          </div>

          {/* Animation Duration */}
          {config.animationType !== 'none' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Animation Duration: <span className="text-gray-600 dark:text-gray-400">{(config.animationDuration || 0.3).toFixed(1)}s</span>
              </label>
              <input
                type="range"
                min="0.1"
                max="2"
                step="0.1"
                value={config.animationDuration || 0.3}
                onChange={(e) => handleChange('animationDuration', parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertControls;
